import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, FileText, Download, TrendingUp, AlertCircle, CheckCircle2, Sparkles, RefreshCw, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { getCreditHeroScoreLink } from '@/utils/affiliate';

const CreateDisputePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('accounts'); // 'accounts' or 'inquiries'
  
  // JSON Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  
  // Account disputes state
  const [negativeAccounts, setNegativeAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  
  // Inquiry disputes state
  const [creditInquiries, setCreditInquiries] = useState([]);
  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [inquiryReason, setInquiryReason] = useState('I did not authorize this inquiry');
  
  // Common state
  const [selectedBureaus, setSelectedBureaus] = useState({
    experian: false,
    equifax: false,
    transunion: false
  });
  const [customReason, setCustomReason] = useState('');
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [hasCreditReport, setHasCreditReport] = useState(false);

  useEffect(() => {
    fetchCreditData();
  }, []);

  const fetchCreditData = async () => {
    try {
      // Fetch both accounts and inquiries
      const [accountsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/credit-report/negative-accounts`),
        axios.get(`${API}/credit-report/inquiries`)
      ]);
      
      if (accountsRes.data.accounts && accountsRes.data.accounts.length > 0) {
        setNegativeAccounts(accountsRes.data.accounts);
        setHasCreditReport(true);
      }
      
      if (inquiriesRes.data.inquiries && inquiriesRes.data.inquiries.length > 0) {
        setCreditInquiries(inquiriesRes.data.inquiries);
        setHasCreditReport(true);
      }
      
      if (accountsRes.data.accounts.length === 0 && inquiriesRes.data.inquiries.length === 0) {
        setHasCreditReport(false);
      }
    } catch (error) {
      console.error('Error fetching credit data:', error);
      setHasCreditReport(false);
    } finally {
      setLoading(false);
    }
  };

  const handleImportJSON = async () => {
    if (!jsonInput.trim()) {
      toast.error('Please paste your credit report JSON');
      return;
    }

    setImporting(true);
    try {
      // Parse JSON to validate
      const creditData = JSON.parse(jsonInput);
      
      // Send to backend
      const response = await axios.post(`${API}/credit-report/import-json`, {
        credit_report_json: creditData
      });

      toast.success(`✅ Import successful! Found ${response.data.accounts_found} negative accounts and ${response.data.inquiries_found} inquiries`);
      
      // Update state with imported data
      if (response.data.negative_accounts && response.data.negative_accounts.length > 0) {
        setNegativeAccounts(response.data.negative_accounts);
        setHasCreditReport(true);
      }
      
      if (response.data.inquiries && response.data.inquiries.length > 0) {
        setCreditInquiries(response.data.inquiries);
        setHasCreditReport(true);
      }

      // Close modal and reset
      setShowImportModal(false);
      setJsonInput('');
      
    } catch (error) {
      if (error.message.includes('JSON')) {
        toast.error('Invalid JSON format. Please check your input.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import credit report');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleClearData = async () => {
    setClearingData(true);
    try {
      await axios.post(`${API}/credit-report/clear`);
      
      // Clear local state
      setNegativeAccounts([]);
      setCreditInquiries([]);
      setSelectedAccounts([]);
      setSelectedInquiries([]);
      setHasCreditReport(false);
      
      toast.success('✅ Credit report data cleared. You can now import a new report.');
      setShowClearDataModal(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearingData(false);
    }
  };

  const handleAccountToggle = (accountId) => {
    setSelectedAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleInquiryToggle = (inquiryId) => {
    setSelectedInquiries(prev =>
      prev.includes(inquiryId)
        ? prev.filter(id => id !== inquiryId)
        : [...prev, inquiryId]
    );
  };

  const handleSelectAllInquiries = () => {
    if (selectedInquiries.length === creditInquiries.length) {
      setSelectedInquiries([]);
    } else {
      setSelectedInquiries(creditInquiries.map(inq => inq.id));
    }
  };

  const handleBureauToggle = (bureau) => {
    setSelectedBureaus(prev => ({
      ...prev,
      [bureau]: !prev[bureau]
    }));
  };

  const handleSelectAllAccounts = () => {
    if (selectedAccounts.length === negativeAccounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(negativeAccounts.map(acc => acc.id));
    }
  };

  const handleSelectAllBureaus = () => {
    const allSelected = Object.values(selectedBureaus).every(v => v);
    setSelectedBureaus({
      experian: !allSelected,
      equifax: !allSelected,
      transunion: !allSelected
    });
  };

  const handleGenerateLetters = async () => {
    const isAccountTab = activeTab === 'accounts';
    const selectedItems = isAccountTab ? selectedAccounts : selectedInquiries;
    
    if (selectedItems.length === 0) {
      toast.error(`Please select at least one ${isAccountTab ? 'account' : 'inquiry'} to dispute`);
      return;
    }

    const selectedBureausList = Object.entries(selectedBureaus)
      .filter(([_, checked]) => checked)
      .map(([bureau]) => bureau);

    if (selectedBureausList.length === 0) {
      toast.error('Please select at least one bureau');
      return;
    }

    setGenerating(true);
    try {
      const endpoint = isAccountTab ? '/disputes/generate-bulk' : '/disputes/generate-inquiry-disputes';
      const payload = isAccountTab 
        ? {
            account_ids: selectedAccounts,
            bureaus: selectedBureausList,
            custom_reason: customReason || null
          }
        : {
            inquiry_ids: selectedInquiries,
            bureaus: selectedBureausList,
            reason: inquiryReason
          };

      const response = await axios.post(`${API}${endpoint}`, payload);

      setGeneratedLetters(response.data.letters);
      toast.success(`Generated ${response.data.letters.length} dispute letters!`);
    } catch (error) {
      toast.error('Failed to generate letters');
    } finally {
      setGenerating(false);
    }
  };

  const downloadLetter = (letter) => {
    const blob = new Blob([letter.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${letter.bureau}_dispute_${letter.account_name}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-xl font-heading">Loading your credit data...</p>
        </div>
      </div>
    );
  }

  // Show Identity IQ prompt if no credit report
  if (!hasCreditReport) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
              <div className="flex items-center gap-3">
                <Button 
                  onClick={fetchCreditData}
                  variant="ghost"
                  className="text-violet-600 hover:text-violet-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </Button>
                <Button onClick={() => navigate('/dashboard')} variant="ghost">
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Dashboard
                </Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-slate-100 text-center">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-10 h-10 text-blue-600" />
            </div>
            
            <h1 className="text-3xl font-heading font-bold mb-4">Get Your Credit Report First</h1>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              To create dispute letters, you need access to your credit reports. 
              Sign up for Identity IQ to get reports from all 3 bureaus.
            </p>

            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 mb-8 text-left">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-bold text-yellow-900 mb-2">Important Information</h3>
                  <ul className="space-y-2 text-sm text-yellow-800">
                    <li>• Identity IQ is a <strong>separate subscription</strong> (~$29.99/month)</li>
                    <li>• NOT included in your $59.99 DisputeKing.AI fee</li>
                    <li>• Paid directly to Identity IQ</li>
                    <li>• Provides access to Experian, Equifax, and TransUnion reports</li>
                    <li>• Using our affiliate link supports DisputeKing.AI at no extra cost</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* New User - Sign Up */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="font-heading font-bold text-lg mb-3">New to Identity IQ?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Sign up for Identity IQ to access your credit reports from all 3 bureaus
                </p>
                <a
                  href={getCreditHeroScoreLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg font-heading">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Sign Up for Identity IQ
                  </Button>
                </a>
              </div>

              {/* Existing User - Login */}
              <div className="bg-violet-50 rounded-2xl p-6">
                <h3 className="font-heading font-bold text-lg mb-3">Already have an Identity IQ account?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Log in to Identity IQ, then come back here and click "Refresh Data" to import your report
                </p>
                <div className="flex gap-3 justify-center">
                  <a
                    href="https://www.identityiq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 py-6 font-heading">
                      Login to IdentityIQ.com
                    </Button>
                  </a>
                  <a
                    href="https://www.myscoreiq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 py-6 font-heading">
                      Login to MyScoreIQ.com
                    </Button>
                  </a>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  After logging in, click the "Refresh Data" button at the top right
                </p>
                <button
                  onClick={() => setShowInstructionsModal(true)}
                  className="text-sm text-violet-600 hover:text-violet-700 underline font-semibold mt-2"
                >
                  📋 Need help? Click here for step-by-step instructions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main dispute creation interface
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            <Button onClick={() => navigate('/dashboard')} variant="ghost">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Import Button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Create Dispute Letters</h1>
            <p className="text-slate-600">Select accounts or inquiries and bureaus to generate AI-powered dispute letters</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowImportModal(true)}
              className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full font-heading font-semibold hover:opacity-90 whitespace-nowrap"
            >
              <FileText className="w-4 h-4 mr-2" />
              Import JSON
            </Button>
            {(negativeAccounts.length > 0 || creditInquiries.length > 0) && (
              <Button
                onClick={() => setShowClearDataModal(true)}
                variant="outline"
                className="rounded-full font-heading whitespace-nowrap border-red-300 text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Clear & Re-Import
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-slate-100 p-1 rounded-2xl w-fit">
            <button
              onClick={() => setActiveTab('accounts')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'accounts'
                  ? 'bg-white text-violet-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Account Disputes ({negativeAccounts.length})
            </button>
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'inquiries'
                  ? 'bg-white text-violet-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Inquiry Disputes ({creditInquiries.length})
            </button>
          </div>
        </div>

        {generatedLetters.length === 0 ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: Accounts/Inquiries Selection */}
            <div className="lg:col-span-2 bg-white rounded-3xl p-6 border-2 border-slate-100">
              {activeTab === 'accounts' ? (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-heading font-bold">Select Accounts to Dispute</h2>
                    <Button
                      onClick={handleSelectAllAccounts}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      {selectedAccounts.length === negativeAccounts.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {negativeAccounts?.map((account) => (
                      <div
                        key={account.id}
                        className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
                          selectedAccounts.includes(account.id)
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                        onClick={() => handleAccountToggle(account.id)}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedAccounts.includes(account.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{account.creditor_name}</h3>
                            <p className="text-sm text-slate-600">Account #: {account.account_number}</p>
                            <div className="mt-2 flex gap-2 flex-wrap">
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                                {account.status}
                              </span>
                              {account.reported_on?.map(bureau => (
                                <span key={bureau} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">
                                  {bureau}
                                </span>
                              ))}
                            </div>
                            {account.balance && (
                              <p className="text-sm text-slate-500 mt-1">Balance: ${account.balance}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-heading font-bold">Select Inquiries to Dispute</h2>
                    <Button
                      onClick={handleSelectAllInquiries}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      {selectedInquiries.length === creditInquiries.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {creditInquiries?.map((inquiry) => (
                      <div
                        key={inquiry.id}
                        className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
                          selectedInquiries.includes(inquiry.id)
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                        onClick={() => handleInquiryToggle(inquiry.id)}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedInquiries.includes(inquiry.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{inquiry.company_name}</h3>
                            <p className="text-sm text-slate-600">Date: {new Date(inquiry.inquiry_date).toLocaleDateString()}</p>
                            <div className="mt-2 flex gap-2 flex-wrap">
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                {inquiry.inquiry_type}
                              </span>
                              {inquiry.reported_on?.map(bureau => (
                                <span key={bureau} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">
                                  {bureau}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Right: Bureau Selection & Actions */}
            <div className="space-y-6">
              {/* Bureau Selection */}
              <div className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-heading font-bold">Select Bureaus</h2>
                  <Button
                    onClick={handleSelectAllBureaus}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                  >
                    {Object.values(selectedBureaus).every(v => v) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                <div className="space-y-3">
                  {[
                    { key: 'experian', label: 'Experian', color: 'bg-blue-100 text-blue-700' },
                    { key: 'equifax', label: 'Equifax', color: 'bg-green-100 text-green-700' },
                    { key: 'transunion', label: 'TransUnion', color: 'bg-purple-100 text-purple-700' }
                  ].map((bureau) => (
                    <div
                      key={bureau.key}
                      className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${
                        selectedBureaus[bureau.key]
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-violet-300'
                      }`}
                      onClick={() => handleBureauToggle(bureau.key)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox checked={selectedBureaus[bureau.key]} />
                        <div className="flex-1">
                          <span className="font-semibold">{bureau.label}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${bureau.color}`}>
                          Bureau
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Reason / Inquiry Reason */}
              <div className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                {activeTab === 'accounts' ? (
                  <>
                    <Label className="mb-2 block">Custom Dispute Reason (Optional)</Label>
                    <Textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="e.g., 'This account is not mine' or 'This was already paid'"
                      className="rounded-xl min-h-[100px]"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Leave blank for AI to generate appropriate reasons
                    </p>
                  </>
                ) : (
                  <>
                    <Label className="mb-2 block">Inquiry Dispute Reason</Label>
                    <select
                      value={inquiryReason}
                      onChange={(e) => setInquiryReason(e.target.value)}
                      className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    >
                      <option value="I did not authorize this inquiry">I did not authorize this inquiry</option>
                      <option value="This inquiry is too old">This inquiry is too old</option>
                      <option value="I never applied for credit with this company">I never applied for credit with this company</option>
                      <option value="This inquiry was made without my permission">This inquiry was made without my permission</option>
                      <option value="I was not seeking credit at this time">I was not seeking credit at this time</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-2">
                      Select the most appropriate reason for disputing these inquiries
                    </p>
                  </>
                )}
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerateLetters}
                disabled={generating || (activeTab === 'accounts' ? selectedAccounts.length === 0 : selectedInquiries.length === 0)}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading text-lg"
              >
                {generating ? (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Generate {(activeTab === 'accounts' ? selectedAccounts.length : selectedInquiries.length) * Object.values(selectedBureaus).filter(v => v).length} Letters
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                {activeTab === 'accounts' ? selectedAccounts.length : selectedInquiries.length} {activeTab === 'accounts' ? 'accounts' : 'inquiries'} × {Object.values(selectedBureaus).filter(v => v).length} bureaus
              </p>
            </div>
          </div>
        ) : (
          /* Generated Letters View */
          <div className="space-y-6">
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <div>
                  <h3 className="font-heading font-bold text-green-900">
                    Success! Generated {generatedLetters.length} Dispute Letters
                  </h3>
                  <p className="text-green-700 text-sm">
                    Download, print, sign, and mail within 24 hours for best results
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {generatedLetters?.map((letter, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="w-8 h-8 text-violet-600" />
                    <div>
                      <h3 className="font-heading font-bold">{letter.bureau}</h3>
                      <p className="text-sm text-slate-600">{letter.account_name}</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => downloadLetter(letter)}
                    className="w-full rounded-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Letter
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => {
                  setGeneratedLetters([]);
                  setSelectedAccounts([]);
                  setSelectedInquiries([]);
                  setSelectedBureaus({ experian: false, equifax: false, transunion: false });
                }}
                variant="outline"
                className="rounded-full"
              >
                Create More Letters
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Import JSON Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-heading font-bold mb-4">Import Identity IQ Credit Report JSON</h2>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-blue-900">📋 How to Get Your JSON:</h3>
                <div className="flex gap-2">
                  <a
                    href="https://www.identityiq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-full font-semibold transition-colors"
                  >
                    Open IdentityIQ.com ↗
                  </a>
                  <a
                    href="https://www.myscoreiq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-full font-semibold transition-colors"
                  >
                    Open MyScoreIQ.com ↗
                  </a>
                </div>
              </div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                <li>Click one of the buttons above to open Identity IQ in a new window</li>
                <li>Log in to your Identity IQ account</li>
                <li>View your credit report</li>
                <li>Right-click anywhere → Select "Inspect" or "Inspect Element"</li>
                <li>Go to the "Sources" tab (or "Debugger" in Firefox)</li>
                <li>Find "Cloud icon imc-us3.csid.co" or similar in the left panel</li>
                <li>Look for a JSON file with credit report data</li>
                <li>Copy the entire JSON content and paste below</li>
              </ol>
              <button
                onClick={() => setShowInstructionsModal(true)}
                className="mt-3 flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-semibold underline"
              >
                <HelpCircle className="w-4 h-4" />
                View detailed step-by-step instructions
              </button>
            </div>

            <Label className="mb-2 block">Paste Your Credit Report JSON:</Label>
            <Textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder='{"accounts": [...], "inquiries": [...], ...}'
              className="min-h-[300px] font-mono text-sm mb-4"
            />

            <div className="flex gap-3">
              <Button
                onClick={handleImportJSON}
                disabled={importing || !jsonInput.trim()}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                {importing ? 'Importing...' : 'Import Credit Report'}
              </Button>
              <Button
                onClick={() => {
                  setShowImportModal(false);
                  setJsonInput('');
                }}
                variant="outline"
                className="flex-1 rounded-full font-heading"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Data Confirmation Modal */}
      {showClearDataModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-heading font-bold mb-4 text-red-600">⚠️ Clear Credit Report Data?</h2>
            
            <p className="text-slate-700 mb-6">
              This will permanently delete your imported credit report data, including:
            </p>
            
            <ul className="list-disc list-inside space-y-2 mb-6 text-slate-600">
              <li>All negative accounts ({negativeAccounts.length})</li>
              <li>All credit inquiries ({creditInquiries.length})</li>
              <li>Import history</li>
            </ul>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                💡 <strong>Why clear data?</strong> Use this if you want to import a newer credit report or if you accidentally imported the wrong data.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleClearData}
                disabled={clearingData}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full font-heading"
              >
                {clearingData ? 'Clearing...' : 'Yes, Clear All Data'}
              </Button>
              <Button
                onClick={() => setShowClearDataModal(false)}
                variant="outline"
                className="flex-1 rounded-full font-heading"
                disabled={clearingData}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions Modal */}
      {showInstructionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-heading font-bold text-gradient">📋 How to Import Your Identity IQ JSON</h2>
              <button
                onClick={() => setShowInstructionsModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="prose prose-slate max-w-none">
              {/* Quick Steps */}
              <div className="bg-gradient-to-br from-violet-50 to-blue-50 rounded-2xl p-6 mb-6">
                <h3 className="text-xl font-heading font-bold mb-4 text-violet-900">⚡ Quick Steps</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-violet-800 mb-2">STEP 1: Get JSON from Identity IQ</h4>
                    <ol className="text-sm space-y-1 text-slate-700">
                      <li>1. Log into IdentityIQ.com or MyScoreIQ.com</li>
                      <li>2. Go to your Credit Report page</li>
                      <li>3. Press <kbd className="px-2 py-1 bg-slate-200 rounded">F12</kbd> to open DevTools</li>
                      <li>4. Click <strong>"Network"</strong> tab</li>
                      <li>5. Refresh page (<kbd className="px-2 py-1 bg-slate-200 rounded">F5</kbd>)</li>
                      <li>6. Type "json" in filter box</li>
                      <li>7. Find request with credit data</li>
                      <li>8. Copy the JSON response</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">STEP 2: Import to DisputeKing.AI</h4>
                    <ol className="text-sm space-y-1 text-slate-700">
                      <li>1. Click "Import JSON" button</li>
                      <li>2. Paste your JSON</li>
                      <li>3. Click "Import Credit Report"</li>
                      <li>4. ✅ Your accounts load!</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Detailed Instructions */}
              <div className="space-y-6">
                <div className="bg-white border-2 border-slate-200 rounded-2xl p-6">
                  <h3 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">1</span>
                    Open Developer Tools
                  </h3>
                  <div className="ml-10 space-y-2 text-sm text-slate-700">
                    <p><strong>Windows/Linux:</strong> Press <kbd className="px-2 py-1 bg-slate-200 rounded">F12</kbd> or <kbd className="px-2 py-1 bg-slate-200 rounded">Ctrl + Shift + I</kbd></p>
                    <p><strong>Mac:</strong> Press <kbd className="px-2 py-1 bg-slate-200 rounded">Cmd + Option + I</kbd></p>
                    <p><strong>Or:</strong> Right-click anywhere → Select "Inspect" or "Inspect Element"</p>
                  </div>
                </div>

                <div className="bg-white border-2 border-slate-200 rounded-2xl p-6">
                  <h3 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">2</span>
                    Find Your JSON Data (Network Tab Method - Easiest)
                  </h3>
                  <div className="ml-10 space-y-2 text-sm text-slate-700">
                    <ol className="list-disc list-inside space-y-2">
                      <li>Click the <strong>"Network"</strong> tab in Developer Tools</li>
                      <li>Refresh the Identity IQ page (F5 or Cmd+R)</li>
                      <li>In the filter/search box, type: <code className="px-2 py-1 bg-slate-100 rounded">json</code> or <code className="px-2 py-1 bg-slate-100 rounded">api</code></li>
                      <li>Click on different requests to preview them</li>
                      <li>Look for one with credit data (creditor names, account numbers)</li>
                      <li>Click the "Response" or "Preview" tab</li>
                      <li>Right-click → "Copy all" or select all (Ctrl+A) and copy (Ctrl+C)</li>
                    </ol>
                  </div>
                </div>

                <div className="bg-white border-2 border-slate-200 rounded-2xl p-6">
                  <h3 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">3</span>
                    Alternative: Sources/Debugger Tab Method
                  </h3>
                  <div className="ml-10 space-y-2 text-sm text-slate-700">
                    <ol className="list-disc list-inside space-y-2">
                      <li>Click "Sources" (Chrome/Edge) or "Debugger" (Firefox) tab</li>
                      <li>Look in left sidebar for domains like <code className="px-2 py-1 bg-slate-100 rounded">imc-us3.csid.co</code></li>
                      <li>Browse files to find JSON with credit information</li>
                      <li>Click file, select all (Ctrl+A), copy (Ctrl+C)</li>
                    </ol>
                  </div>
                </div>

                <div className="bg-white border-2 border-slate-200 rounded-2xl p-6">
                  <h3 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold">4</span>
                    Import into DisputeKing.AI
                  </h3>
                  <div className="ml-10 space-y-2 text-sm text-slate-700">
                    <ol className="list-disc list-inside space-y-2">
                      <li>Close this modal and click the purple "Import JSON" button</li>
                      <li>Paste your copied JSON into the text area</li>
                      <li>Click "Import Credit Report"</li>
                      <li>✅ Success! Your negative accounts and inquiries will appear</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 mt-6">
                <h3 className="text-lg font-heading font-bold mb-3 text-yellow-900">💡 Pro Tips</h3>
                <ul className="space-y-2 text-sm text-yellow-800">
                  <li>✓ Keep Developer Tools open BEFORE loading your credit report - easier to catch API calls</li>
                  <li>✓ The JSON will be LONG (thousands of lines) - that's normal!</li>
                  <li>✓ Look for words like "accounts", "tradelines", or "inquiries" in the JSON</li>
                  <li>✓ Make sure you copy EVERYTHING from the first <code className="px-1 bg-yellow-100">&#123;</code> to the last <code className="px-1 bg-yellow-100">&#125;</code></li>
                  <li>✓ If you see creditor names and account numbers, you found the right JSON!</li>
                </ul>
              </div>

              {/* Troubleshooting */}
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mt-6">
                <h3 className="text-lg font-heading font-bold mb-3 text-red-900">🔧 Common Issues</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-red-800">Can't find any JSON data?</p>
                    <p className="text-red-700">→ Refresh the Identity IQ page WITH Developer Tools open, try Network tab method</p>
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">Import failed - Invalid JSON?</p>
                    <p className="text-red-700">→ Make sure you copied the ENTIRE JSON (including all brackets). Don't copy HTML or other text.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">Import successful but no accounts showing?</p>
                    <p className="text-red-700">→ The JSON format might not match. Check browser console for errors or contact support.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <Button
                onClick={() => {
                  setShowInstructionsModal(false);
                  setShowImportModal(true);
                }}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                Got it! Open Import Modal
              </Button>
              <Button
                onClick={() => setShowInstructionsModal(false)}
                variant="outline"
                className="rounded-full font-heading"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDisputePage;
